/**
 * Phase 5c — SSE chat client.
 *
 * The Anthropic-style SSE wire-format used by `/api/ai/chat`:
 *
 *   event: meta              data: { conversation_id, user_message_id, model, tools_count, role, attachment_count }
 *   event: token             data: { text }
 *   event: tool_use          data: { id, name, input }
 *   event: tool_result       data: { tool_use_id, name, result }
 *   event: confirmation_required  data: { tool, preview, token, expires_in_min }
 *   event: done              data: { assistant_message_id, conversation_id, usage, cost_usd, iterations, confirmations_requested }
 *   event: error             data: { error, detail? }
 *
 * Native EventSource doesn't support custom headers (we need Bearer auth), so
 * we use fetch + ReadableStream and parse SSE chunks manually.
 *
 * iter-17 additions:
 *   - `uploadAttachment(file)` and `uploadAttachments(files)` for chat file
 *     uploads. These return `{file_id, content_type, size_bytes, ...}` rows
 *     that can then be passed to `streamAiChat({attachments:[file_id, ...]})`.
 */

import { API_BASE, getToken } from "@/lib/api";

const DECODER = new TextDecoder("utf-8");

/**
 * @param {Object} opts
 * @param {string} opts.message
 * @param {string|null} [opts.conversationId]
 * @param {string|null} [opts.title]
 * @param {string[]} [opts.attachments]   // ai_attachments file_ids
 * @param {AbortSignal} [opts.signal]
 * @param {(evt: {event: string, data: any}) => void} opts.onEvent
 * @returns {Promise<void>}
 */
export async function streamAiChat({
  message,
  conversationId = null,
  title = null,
  attachments = null,
  // PR-Cg-Phase7A — effort tier. Server default is ``"high"``; when
  // omitted the backend falls back to that default so pre-Phase7A
  // clients keep working. Accepts ``"medium"`` | ``"high"`` | ``"max"``.
  effort = null,
  signal,
  onEvent,
  // E6 — internal flag prevents an infinite retry loop. Public callers
  // never set it; we flip it false on the auto-retry path below.
  _retryAfterStaleConversation = true,
}) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/ai/chat`, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      message,
      conversation_id: conversationId,
      title,
      attachments: attachments && attachments.length ? attachments : undefined,
      effort: effort || undefined,
    }),
  });

  if (!res.ok) {
    let detail = "";
    try {
      const j = await res.json();
      detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail || j);
    } catch (e) {
      detail = await res.text().catch(() => res.statusText);
    }
    // E6 chatbot 404 fix — when the client carries a stale `conversationId`
    // (the conversation was deleted on the backend, but the id still lives
    // in localStorage / parent state), the backend correctly 404s with
    // `conversation_not_found`. Silently recover by retrying ONCE with a
    // null conversation_id; the backend mints a fresh one and the new id
    // is broadcast through the standard `meta` event so the host
    // component's localStorage gets overwritten.
    if (
      res.status === 404 &&
      conversationId &&
      _retryAfterStaleConversation &&
      /conversation_not_found/.test(detail)
    ) {
      return streamAiChat({
        message,
        conversationId: null,
        title,
        attachments,
        effort,
        signal,
        onEvent,
        _retryAfterStaleConversation: false,
      });
    }
    onEvent({
      event: "error",
      data: { error: `http_${res.status}`, detail },
    });
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    onEvent({ event: "error", data: { error: "no_response_body" } });
    return;
  }

  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += DECODER.decode(value, { stream: true });

    // SSE messages are separated by a blank line (\n\n).
    while (true) {
      const idx = buffer.indexOf("\n\n");
      if (idx === -1) break;
      const raw = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);

      let evt = "message";
      const dataLines = [];
      for (const line of raw.split("\n")) {
        if (!line) continue;
        if (line.startsWith(":")) continue;
        if (line.startsWith("event:")) {
          evt = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trimStart());
        }
      }

      const dataStr = dataLines.join("\n");
      let data = null;
      if (dataStr) {
        try {
          data = JSON.parse(dataStr);
        } catch (e) {
          data = { raw: dataStr };
        }
      }
      onEvent({ event: evt, data });
    }
  }
}

export async function fetchConversations() {
  const token = getToken();
  const res = await fetch(`${API_BASE}/ai/conversations`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchConversation(id) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/ai/conversations/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    // E6 — attach the HTTP status to the error so callers can detect
    // "conversation deleted on server" (404) without parsing strings.
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function deleteConversation(id) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/ai/conversations/${id}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function patchConversation(id, body) {
  const token = getToken();
  const res = await fetch(`${API_BASE}/ai/conversations/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function fetchToolSummary() {
  const token = getToken();
  const res = await fetch(`${API_BASE}/ai/tool-summary`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ─── Attachments (iter-17) ─────────────────────────────────────────────-

/** Per-file limits — must match backend `services/ai_attachments.py`. */
export const ATTACHMENT_LIMITS = {
  MAX_FILES: 5,
  MAX_BYTES: 10 * 1024 * 1024,
  ACCEPTED_TYPES: [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/gif",
    "application/pdf",
  ],
};

export function isAcceptedFile(file) {
  return ATTACHMENT_LIMITS.ACCEPTED_TYPES.includes(file.type);
}

export function bytesToHuman(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Upload one file. Returns the canonical `ai_attachments` row. */
export async function uploadAttachment(file) {
  const token = getToken();
  const fd = new FormData();
  fd.append("files", file, file.name);
  const res = await fetch(`${API_BASE}/ai/attachments`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    let detail = "";
    try {
      const j = await res.json();
      detail =
        typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail || j);
    } catch (_) {
      detail = await res.text().catch(() => res.statusText);
    }
    throw new Error(`upload_failed: ${detail}`);
  }
  const arr = await res.json();
  return arr[0];
}

/** Upload many files in parallel. Returns array of attachment rows in input
 * order (or rejects on first failure). */
export async function uploadAttachments(files) {
  return Promise.all(Array.from(files).map(uploadAttachment));
}
